import * as React from 'react';

import { faArrowRightFromBracket, faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as z from 'zod';

import { useQuickAction } from 'src/features/cards/api/useQuickAction';
import { cardMessages } from 'src/features/cards/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { toast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import { generalErrorMessages, generalMessages } from 'src/utils/messages';

import type { InParkingCard } from 'src/features/cards/types/card';

const quickActionFormSchema = z.object({
    in_parking_card_id: z.number(),
    // in_parking_card_id: z.string().min(1),
});
type QuickActionFormValues = z.infer<typeof quickActionFormSchema>;

interface QuickActionModalFormProps extends React.HTMLAttributes<HTMLDivElement> {
    quickActionId: string;
    refetch: () => void;
    inParkingCardDetail?: InParkingCard | null;
}

const QuickActionModalForm = ({ quickActionId, children, refetch, inParkingCardDetail }: QuickActionModalFormProps) => {
    const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const { mutate: performQuickAction, isPending } = useQuickAction({
        onSuccess: (_ciw) => {
            refetch();
            onOpenChange(false);
            toast({
                title: formatMessage(cardMessages.quickActionSuccessful),
                description: formatMessage(cardMessages.quickActionSuccessMessage, {
                    client: null,
                }),
            });
        },
        onError: (error) => {
            const errorData = error.response?.data;
            const errorCode = errorData?.code ?? formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorData?.message ?? formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const form = useForm<QuickActionFormValues>({
        resolver: zodResolver(quickActionFormSchema),
        defaultValues: {
            in_parking_card_id: inParkingCardDetail?.id,
        },
    });

    const onSubmit = (values: QuickActionFormValues) => {
        const submitData = {
            parkingId,
            quickActionId,
            ...values,
        };

        performQuickAction(submitData);
    };

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) {
            form.reset();
        }
        setIsModalOpen(isOpen);
    };

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children}
            <DialogContent>
                <DialogHeader>
                    <div className="flex gap-4">
                        <SquareIcon icon={faArrowRightFromBracket} />
                        <div>
                            <DialogTitle>{formatMessage(cardMessages.quickAction)}</DialogTitle>
                            <DialogDescription>
                                {formatMessage(cardMessages.quickActionPerform, {
                                    client: null,
                                })}
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>{formatMessage(cardMessages.quickActionPerformConfirmation)}</DialogBody>
                <DialogSeparator />

                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            {formatMessage(generalMessages.cancel)}
                        </Button>
                    </DialogClose>

                    <Button disabled={isPending} type="submit" onClick={form.handleSubmit(onSubmit)} className="flex-1">
                        {isPending ? (
                            <FontAwesomeIcon icon={faSpinnerThird} className="mr-2 size-4 animate-spin" />
                        ) : null}
                        {formatMessage(generalMessages.confirm)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export { QuickActionModalForm, quickActionFormSchema };
export type { QuickActionFormValues };
